import axios from "axios";
import store from "../store";
import { showErroMessage } from "../../LayoutContainers/AppLayout/redux/AppSlice";
import { API_BASE_URL } from "../../utils/constants";

const AppService = axios.create({
  baseURL: API_BASE_URL,
});

// Debug helper: only logs when localStorage.DEBUG_API === '1'
const debug = (...args) => {
  try {
    if (typeof window !== 'undefined' && window.localStorage && localStorage.getItem('DEBUG_API') === '1') {
      // eslint-disable-next-line no-console
      console.log(...args);
    }
  } catch (_) { /* ignore */ }
};

AppService.interceptors.response.use(
  (response) => {
    return response.data;
  },
  function (error) {
    // Any status codes that falls outside the range of 2xx cause this function to trigger
    // Do something with response error
    console.error(error);
    let errorMessage = error?.response?.data?.message || "something went wrong";
    store.dispatch(showErroMessage(errorMessage));
    return Promise.reject(error);
  }
);

AppService.interceptors.request.use(async function (config) {
  debug('Making API request to:', config.url);
  
  try {
    // First try to get token from authInfo for backward compatibility
    let authInfo = localStorage.getItem("authInfo");
    let token = null;
    
    if (authInfo != null) {
      try {
        const parsedAuthInfo = JSON.parse(authInfo);
        debug('Auth info found');
        
        if (parsedAuthInfo.token) {
          debug('Token found in authInfo');
          token = parsedAuthInfo.token;
        }
      } catch (error) {
        console.error('Error parsing auth info:', error);
      }
    }
    
    // If no token in authInfo, try to get from jwt_token
    if (!token) {
      token = localStorage.getItem('jwt_token');
      if (token) {
        debug('Token found in jwt_token');
      }
    }

    // If still no token, try to get from standalone token key
    if (!token) {
      token = localStorage.getItem('token');
      if (token) {
        debug('Token found in token key');
      }
    }
    
    // If we have a token, validate it
    if (token) {
      try {
        // Dynamically import auth.js to avoid circular dependencies
        const auth = await import('../../services/auth');
        
        // Check if token is valid
        if (auth.isTokenValid(token)) {
          debug('Token is valid, adding to request');
          config.headers.Authorization = "Bearer " + token;
          // Show success popup only occasionally (1 in 10 requests) to avoid too many popups
          if (Math.random() < 0.1) {
            auth.showTokenStatusPopup(true, 'Authentication token is valid');
          }
        } else {
          debug('Token is invalid, attempting to refresh...');
          // Try to refresh the token
          const newToken = await auth.validateToken(false); // Don't show popup here
          if (newToken) {
            debug('Token refreshed successfully, adding to request');
            config.headers.Authorization = "Bearer " + newToken;
            // Show success popup for token refresh
            auth.showTokenStatusPopup(true, 'Authentication token refreshed successfully');
          } else {
            debug('Failed to refresh token');
            // Show error popup for token refresh failure
            auth.showTokenStatusPopup(false, 'Authentication failed. Please log in again.');
          }
        }
      } catch (validationError) {
        console.error('Error validating token:', validationError);
      }
    } else {
      debug('No token found in localStorage');
    }
  } catch (error) {
    console.error('Error in request interceptor:', error);
  }
  
  return config;
});

export default AppService;
